import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import TestCloseButton from "../components/TestCloseButton";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Compass, ArrowUp, ArrowDown, CaretLeft, CaretRight, CheckCircle, Trophy,
  WarningCircle,
} from "@phosphor-icons/react";

/**
 * Loopbaanwaardetest — 6 sequential ranking screens. The coachee reorders
 * 5-6 values per screen via up/down arrows. The ranking is auto-saved at
 * every reorder (debounced) and on "Volgende" click.
 */
export default function CoacheeCareerValuesPage() {
  const [coachee, setCoachee] = useState(null);
  const [data, setData] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [order, setOrder] = useState([]); // local ordering of value keys for current screen
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (own) {
          const { data: payload } = await api.get(`/coachees/${own.id}/career-values`);
          setData(payload);
          // Jump to the first unfinished screen by default.
          const firstUndone = payload.screens.findIndex(
            (s) => !payload.completed_screens.includes(s.id),
          );
          setCurrentIdx(firstUndone >= 0 ? firstUndone : 0);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const screen = data?.screens?.[currentIdx];

  // Reset local order whenever the screen changes (or data first loads).
  useEffect(() => {
    if (!screen) return;
    const stored = data?.rankings?.[screen.id];
    if (stored && stored.length === screen.values.length) {
      setOrder(stored);
    } else {
      setOrder(screen.values.map((v) => v.key));
    }
    setSavedAt(null);
  }, [currentIdx, data?.rankings, screen]);

  const valueByKey = useMemo(() => {
    const m = {};
    (screen?.values || []).forEach((v) => { m[v.key] = v; });
    return m;
  }, [screen]);

  const move = (idx, delta) => {
    setOrder((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // Auto-save when `order` changes (debounced).
  useEffect(() => {
    if (!coachee || !screen || order.length === 0) return;
    const stored = data?.rankings?.[screen.id];
    if (stored && stored.length === order.length && stored.every((k, i) => k === order[i])) {
      return; // no change vs persisted
    }
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const { data: updated } = await api.put(
          `/coachees/${coachee.id}/career-values/${screen.id}`,
          { ranking: order },
        );
        setData(updated);
        setSavedAt(new Date());
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const goPrev = () => setCurrentIdx((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIdx((i) => Math.min((data?.screens?.length || 1) - 1, i + 1));

  const isLastScreen = data && currentIdx === data.screens.length - 1;
  const allCompleted = data?.completed === true;

  return (
    <AppLayout title="Loopbaanwaardetest" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Loopbaanwaardetest" }]}>
      <div className="max-w-3xl space-y-6" data-testid="career-values-page">
        {loading ? (
          <div className="text-zinc-500">Laden...</div>
        ) : !coachee ? (
          <div className="text-zinc-500">Coachee-profiel niet gevonden.</div>
        ) : !data ? null : (
          <>
            <IntroAndProgress data={data} currentIdx={currentIdx} onJump={setCurrentIdx} />

            {error && (
              <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="career-values-error">
                <WarningCircle size={18} className="shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            {screen && (
              <ScreenCard
                screen={screen}
                order={order}
                valueByKey={valueByKey}
                onMove={move}
                saving={saving}
                savedAt={savedAt}
              />
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button
                type="button"
                onClick={goPrev}
                disabled={currentIdx === 0}
                data-testid="career-values-prev"
                className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:border-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <CaretLeft size={14} /> Vorige scherm
              </button>
              {!isLastScreen ? (
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="career-values-next"
                  className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-5 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
                >
                  Volgende scherm <CaretRight size={14} />
                </button>
              ) : (
                <div className="text-xs text-zinc-500">
                  Laatste scherm — sla je ranking op via de pijltjes hierboven, dan ben je klaar.
                </div>
              )}
            </div>

            {allCompleted && <CompletedCard data={data} />}
            {allCompleted && <TestCloseButton testId="career-values-close-btn" />}
          </>
        )}
      </div>
    </AppLayout>
  );
}

function IntroAndProgress({ data, currentIdx, onJump }) {
  const done = data.completed_screens.length;
  const total = data.screen_count;
  return (
    <div className="neuro-card-bold p-5 bg-white" data-testid="career-values-intro">
      <div className="flex items-start gap-3 flex-wrap">
        <Compass size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-display text-lg font-bold tracking-tight">Loopbaanwaardetest</h2>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            Op {total} schermen zet je telkens een groep waarden op de volgorde die het beste bij jou
            past — bovenaan wat voor jou het belangrijkste is. Je voortgang wordt automatisch opgeslagen.
          </p>
        </div>
        <div className="text-xs text-zinc-500 shrink-0" data-testid="career-values-progress">
          {done} / {total} schermen
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="career-values-dots">
        {data.screens.map((s, i) => {
          const isDone = data.completed_screens.includes(s.id);
          const isCurrent = i === currentIdx;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onJump(i)}
              data-testid={`career-values-dot-${i}`}
              title={s.title}
              className={
                "inline-flex items-center justify-center h-8 min-w-[2rem] px-2 text-xs font-semibold rounded-sm border-2 transition-colors " +
                (isCurrent
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : isDone
                    ? "bg-[#43A047]/10 text-[#2E7D32] border-[#43A047]/40 hover:border-[#43A047]"
                    : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400")
              }
            >
              {isDone && !isCurrent ? <CheckCircle size={14} weight="fill" /> : i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScreenCard({ screen, order, valueByKey, onMove, saving, savedAt }) {
  return (
    <div className="neuro-card p-5 bg-white" data-testid={`career-values-screen-${screen.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Scherm {screen.order} van 6
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1">{screen.title}</h3>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{screen.subtitle}</p>
        </div>
        <SaveIndicator saving={saving} savedAt={savedAt} />
      </div>
      <div className="mt-4 space-y-2" data-testid="career-values-list">
        {order.map((key, idx) => {
          const v = valueByKey[key];
          if (!v) return null;
          return (
            <div
              key={key}
              className="flex items-stretch gap-2 border-2 border-zinc-200 rounded-sm bg-white hover:border-zinc-400 transition-colors"
              data-testid={`career-values-item-${key}`}
            >
              <div className="flex flex-col items-center justify-center px-2 bg-zinc-50 border-r border-zinc-200">
                <span className="font-display text-lg font-bold text-zinc-400">{idx + 1}</span>
              </div>
              <div className="flex-1 py-2.5 px-3 min-w-0">
                <div className="font-semibold text-zinc-900 text-sm">{v.label}</div>
                {v.description && (
                  <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">{v.description}</p>
                )}
              </div>
              <div className="flex flex-col items-stretch border-l border-zinc-200">
                <button
                  type="button"
                  onClick={() => onMove(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Omhoog"
                  data-testid={`career-values-up-${key}`}
                  className="h-1/2 px-3 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-b border-zinc-200"
                >
                  <ArrowUp size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => onMove(idx, 1)}
                  disabled={idx === order.length - 1}
                  aria-label="Omlaag"
                  data-testid={`career-values-down-${key}`}
                  className="h-1/2 px-3 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowDown size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SaveIndicator({ saving, savedAt }) {
  if (saving) return <span className="text-xs text-zinc-500" data-testid="career-values-saving">Opslaan...</span>;
  if (savedAt) return <span className="text-xs text-[#2E7D32]" data-testid="career-values-saved">✓ Opgeslagen</span>;
  return null;
}

function CompletedCard({ data }) {
  return (
    <div className="neuro-card-bold p-5 bg-white" data-testid="career-values-completed">
      <div className="flex items-start gap-3">
        <Trophy size={26} weight="duotone" className="text-[#FB8C00] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">Loopbaanwaardetest voltooid</h3>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            Alle {data.screen_count} schermen zijn ingevuld. Je rankings zijn opgeslagen en
            beschikbaar voor je work coach. Je kunt op elk moment terug en aanpassingen maken.
          </p>
          <Link
            to="/tests"
            className="inline-flex items-center gap-1.5 mt-3 text-xs uppercase tracking-widest text-zinc-700 hover:text-zinc-900"
          >
            <CaretLeft size={12} /> Terug naar tests
          </Link>
        </div>
      </div>
    </div>
  );
}
